import { useEffect, useState } from "react";
import { OfficerLayout } from "@/components/OfficerLayout";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/i18n";
import { Boxes, ShieldAlert, Landmark, ClipboardCheck, CheckCircle2, FileText } from "lucide-react";

const TABS = [
  { k: "assets", label: "Assets", icon: Boxes },
  { k: "encroachments", label: "Encroachment", icon: ShieldAlert },
  { k: "land", label: "Govt Land", icon: Landmark },
  { k: "inspections", label: "Inspections", icon: ClipboardCheck },
  { k: "ucs", label: "Utilization Certs", icon: FileText },
];

export default function AssetsAll() {
  const { lang } = useLang();
  const { user } = useAuth();
  const [tab, setTab] = useState("assets");
  return (
    <OfficerLayout>
      <div>
        <div className="mb-5">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722]">Assets &amp; GIS</div>
          <h1 className="mt-2 font-heading text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Boxes className="w-7 h-7 text-[#0A3161]" />
            {lang === "hi" ? "संपत्ति प्रबंधन" : "Asset Management"}
          </h1>
        </div>
        <div className="flex border-b border-slate-200 mb-4 overflow-x-auto">
          {TABS.map((tp) => {
            const I = tp.icon;
            return (
              <button key={tp.k} onClick={() => setTab(tp.k)} data-testid={`am-tab-${tp.k}`}
                className={`px-5 py-3 text-sm font-semibold border-b-2 inline-flex items-center gap-1.5 whitespace-nowrap ${tab === tp.k ? "border-[#E87722] text-[#0A3161]" : "border-transparent text-slate-500"}`}>
                <I className="w-4 h-4" /> {tp.label}
              </button>
            );
          })}
        </div>
        {tab === "assets" && <Assets user={user} />}
        {tab === "encroachments" && <Encroachments user={user} />}
        {tab === "land" && <GovtLand user={user} />}
        {tab === "inspections" && <Inspections user={user} />}
        {tab === "ucs" && <UCs user={user} />}
      </div>
    </OfficerLayout>
  );
}

function Assets({ user }) {
  const [rows, setRows] = useState([]);
  const load = () => {
    const params = user?.panchayat_id ? { panchayat_id: user.panchayat_id } : {};
    api.get("/assets", { params }).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); }, [user]);
  const verify = async (id) => {
    await api.post(`/assets/${id}/verify`, {});
    load();
  };
  return (
    <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
      <table className="w-full text-sm" data-testid="assets-table">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          <tr><th className="text-left px-4 py-3">Asset No.</th><th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Name / Location</th><th className="text-right px-4 py-3">Cost</th>
              <th className="text-left px-4 py-3">Condition</th><th className="text-left px-4 py-3">Verified</th>
              <th className="text-right px-4 py-3">Actions</th></tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id} className="border-t border-slate-100">
              <td className="px-4 py-3 font-mono text-xs">{a.asset_no}</td>
              <td className="px-4 py-3 capitalize">{a.asset_type}</td>
              <td className="px-4 py-3"><div className="font-medium">{a.name}</div><div className="text-xs text-slate-500">{a.location}</div></td>
              <td className="px-4 py-3 text-right font-mono">{formatINR(a.cost)}</td>
              <td className="px-4 py-3"><span className={`text-[10px] uppercase px-2 py-0.5 rounded font-semibold ${a.condition === "good" ? "bg-emerald-50 text-emerald-700" : a.condition === "fair" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}`}>{a.condition}</span></td>
              <td className="px-4 py-3">{a.verified ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : "—"}</td>
              <td className="px-4 py-3 text-right">
                {!a.verified && <button onClick={() => verify(a.id)} data-testid={`asset-verify-${a.id}`} className="px-2.5 py-1 text-xs font-semibold rounded-md border border-[#0A3161] text-[#0A3161]">Verify (GPS+Photo)</button>}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-slate-400">No assets</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Encroachments({ user }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const params = user?.panchayat_id ? { panchayat_id: user.panchayat_id } : {};
    api.get("/encroachments", { params }).then((r) => setRows(r.data));
  }, [user]);
  return (
    <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
      <table className="w-full text-sm" data-testid="encroachments-table">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          <tr><th className="text-left px-4 py-3">Enc. No.</th><th className="text-left px-4 py-3">Location</th>
              <th className="text-left px-4 py-3">Type</th><th className="text-left px-4 py-3">Encroacher</th>
              <th className="text-right px-4 py-3">Area (sqft)</th><th className="text-left px-4 py-3">Status</th></tr>
        </thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.id} className="border-t border-slate-100">
              <td className="px-4 py-3 font-mono text-xs">{e.enc_no}</td>
              <td className="px-4 py-3"><div className="font-medium">{e.location}</div><div className="text-xs text-slate-500">{e.description}</div></td>
              <td className="px-4 py-3 capitalize">{e.land_type?.replace("_", " ")}</td>
              <td className="px-4 py-3">{e.encroacher_name || "—"}</td>
              <td className="px-4 py-3 text-right font-mono">{e.area_sqft}</td>
              <td className="px-4 py-3"><span className="text-[10px] uppercase px-2 py-0.5 rounded font-semibold bg-amber-50 text-amber-700">{e.status?.replace("_", " ")}</span></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-400">No encroachments</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function GovtLand({ user }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const params = user?.panchayat_id ? { panchayat_id: user.panchayat_id } : {};
    api.get("/govt-land", { params }).then((r) => setRows(r.data));
  }, [user]);
  return (
    <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
      <table className="w-full text-sm" data-testid="govtland-table">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          <tr><th className="text-left px-4 py-3">Khasra No.</th><th className="text-left px-4 py-3">Type</th>
              <th className="text-right px-4 py-3">Area (Acre)</th><th className="text-left px-4 py-3">Current Use</th></tr>
        </thead>
        <tbody>
          {rows.map((l) => (
            <tr key={l.id} className="border-t border-slate-100">
              <td className="px-4 py-3 font-mono text-xs">{l.khasra_no}</td>
              <td className="px-4 py-3 capitalize">{l.land_type?.replace("_", " ")}</td>
              <td className="px-4 py-3 text-right font-mono">{l.area_acre}</td>
              <td className="px-4 py-3">{l.current_use || "—"}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={4} className="py-10 text-center text-slate-400">No records</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Inspections({ user }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const params = user?.panchayat_id ? { panchayat_id: user.panchayat_id } : {};
    api.get("/inspections", { params }).then((r) => setRows(r.data));
  }, [user]);
  return (
    <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
      <table className="w-full text-sm" data-testid="inspections-table">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          <tr><th className="text-left px-4 py-3">Inspection No.</th><th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Inspector</th>
              <th className="text-left px-4 py-3">Findings</th><th className="text-left px-4 py-3">Rating</th></tr>
        </thead>
        <tbody>
          {rows.map((i) => (
            <tr key={i.id} className="border-t border-slate-100">
              <td className="px-4 py-3 font-mono text-xs">{i.inspection_no}</td>
              <td className="px-4 py-3 capitalize">{i.inspection_type}</td>
              <td className="px-4 py-3 text-xs">{i.inspection_date}</td>
              <td className="px-4 py-3">{i.inspector_name}</td>
              <td className="px-4 py-3 text-xs max-w-md">{i.findings}</td>
              <td className="px-4 py-3"><span className={`text-[10px] uppercase px-2 py-0.5 rounded font-semibold ${i.rating === "satisfactory" ? "bg-emerald-50 text-emerald-700" : i.rating === "needs_improvement" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}`}>{i.rating?.replace("_", " ")}</span></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-400">No inspections</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function UCs({ user }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const params = user?.panchayat_id ? { panchayat_id: user.panchayat_id } : {};
    api.get("/ucs", { params }).then((r) => setRows(r.data));
  }, [user]);
  return (
    <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
      <table className="w-full text-sm" data-testid="ucs-table">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          <tr><th className="text-left px-4 py-3">UC No.</th><th className="text-left px-4 py-3">Scheme</th>
              <th className="text-left px-4 py-3">FY</th><th className="text-right px-4 py-3">Received</th>
              <th className="text-right px-4 py-3">Utilized</th><th className="text-right px-4 py-3">Balance</th></tr>
        </thead>
        <tbody>
          {rows.map((u) => (
            <tr key={u.id} className="border-t border-slate-100">
              <td className="px-4 py-3 font-mono text-xs">{u.uc_no}</td>
              <td className="px-4 py-3"><div className="font-medium">{u.scheme}</div><div className="text-xs text-slate-500">{u.purpose}</div></td>
              <td className="px-4 py-3 font-mono text-xs">{u.financial_year}</td>
              <td className="px-4 py-3 text-right font-mono">{formatINR(u.fund_received)}</td>
              <td className="px-4 py-3 text-right font-mono">{formatINR(u.fund_utilized)}</td>
              <td className="px-4 py-3 text-right font-mono font-bold text-[#E87722]">{formatINR(u.balance)}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-400">No UC records</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
